import type { PortalProfileDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PasswordSetForm } from '../components/PasswordSetForm';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalPasswordService from '../services/portalPasswordService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * The signed-in user's sign-in name for the form's hidden password-manager
 * field: their email, or their phone when they have no email.
 */
function signInNameOf(profile: PortalProfileDto | null): string | null {
	if (profile == null) {
		return null;
	}
	if (profile.email != null && profile.email.trim() !== '') {
		return profile.email;
	}
	return profile.phoneNumber != null && profile.phoneNumber.trim() !== ''
		? profile.phoneNumber
		: null;
}

/**
 * Authenticated change-password page used from Account → "Change Password".
 * Reuses `PasswordSetForm` so the rules and validation match the
 * forgot-password / set-initial flows. The "current password" field is
 * required because login verifies it before rotating the new one.
 */
export function ChangePasswordPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token, isAuthenticated, profile } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);

	// Signed-out visitors get the login modal over an empty page rather than
	// the change-password form, matching the sibling account pages (PD-14185).
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const handleSubmit = async (values: {
		currentPassword: string;
		newPassword: string;
	}): Promise<void> => {
		if (token == null) {
			setSubmitError('Your session has expired. Please sign in again.');
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		try {
			await portalPasswordService.changePassword(
				token,
				values.currentPassword,
				values.newPassword,
			);
			setDone(true);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : 'Unable to change password';
			setSubmitError(message);
		} finally {
			setSubmitting(false);
		}
	};

	if (!isAuthenticated) {
		return <div />;
	}

	if (done) {
		return (
			<div className="px-4 py-8 max-w-md mx-auto">
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
					<h1 className="text-2xl font-semibold text-default">
						{t('Password updated')}
					</h1>
					<p className="text-sm text-brand-muted">
						{t('Your password has been changed.')}
					</p>
					<button
						type="button"
						onClick={() => navigate('/account')}
						className="inline-block rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-4 py-2 transition-colors"
					>
						{t('Back to account')}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="px-4 py-8 max-w-md mx-auto">
			<div className="bg-brand-surface border border-brand-border rounded-xl p-6">
				<PasswordSetForm
					title="Change password"
					subtitle="Enter your current password, then choose a new one."
					requireCurrentPassword
					submitLabel="Update password"
					onSubmit={handleSubmit}
					errorMessage={submitError}
					isSubmitting={submitting}
					username={signInNameOf(profile)}
				/>
			</div>
		</div>
	);
}
